import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { useAlertModal } from '../../AlertModalContext';

const WHATSAPP_MESSAGE_LIMIT = 300;

const getInitials = (nome = '') => {
  const partes = nome.trim().split(' ').filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0].substring(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
};

const avatarColors = ['#2563eb', '#16a34a', '#d97706', '#db2777', '#7c3aed', '#0891b2'];
const getAvatarColor = (id) => avatarColors[id % avatarColors.length];

const CommunicationModal = ({
  show,
  onHide,
  appointments,
  pacientesList,
  todosMedicos,
  especialidades,
  currentEntidade,
  currentUser,
  onSendComplete,
  mode = 'agenda', // Novo prop: 'agenda' ou 'comunicado'
}) => {
  const { showAlertModal } = useAlertModal();

  // --- Estados Internos do Modal ---
  const [communicationMethod, setCommunicationMethod] = useState('email');
  const [whatsAppMessage, setWhatsAppMessage] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [selectedPatients, setSelectedPatients] = useState({});
  const [selectedRecipient, setSelectedRecipient] = useState('todos');
  const [selectAll, setSelectAll] = useState(false);
  const [isLoadingBulk, setIsLoadingBulk] = useState(false);

  // Memoiza a lista de agendamentos que podem receber comunicação
  const listItems = useMemo(() => {
    if (mode === 'comunicado') {
      // No modo comunicado, a lista são todos os pacientes
      return (pacientesList || []).map(p => ({
        id: p.id, // Usamos o ID do paciente como chave
        paciente: p, // O objeto paciente completo
        // Valores placeholder para colunas que não se aplicam
        hora_marcada: '--:--',
        tipo: 'Comunicado',
        status: 'Cadastrado',
      }));
    }
    // Modo padrão 'agenda'
    return (appointments || [])
      .filter(agendamento => agendamento.status === 'Agendada');
  }, [mode, appointments, pacientesList]);

  // Efeito para resetar o estado interno quando o modal é aberto
  useEffect(() => {
    if (show) {
      const isAgendaMode = mode === 'agenda';
      const defaultMessage = isAgendaMode ? `Olá, {paciente_nome}! Lembrete do seu agendamento de {especialidade_nome} com Dr(a). {medico_nome} para o dia {data_agendamento} às {hora_marcada}.` : `Olá, {paciente_nome}! Temos um comunicado importante da ${currentEntidade?.nome || 'Clínica'}.`;
      const defaultEmailSubject = isAgendaMode ? `Lembrete de Agendamento - ${currentEntidade?.nome || 'Clínica'}` : `Comunicado Importante - ${currentEntidade?.nome || 'Clínica'}`;
      const defaultEmailBody = isAgendaMode ? `Olá, {paciente_nome}!\n\nEste é um lembrete do seu agendamento de {especialidade_nome} com Dr(a). {medico_nome} para o dia {data_agendamento} às {hora_marcada}.\n\nAtenciosamente,\nEquipe ${currentEntidade?.nome || 'da Clínica'}.` : `Olá, {paciente_nome}!\n\nEste é um comunicado importante da ${currentEntidade?.nome || 'Clínica'}.\n\n[Insira seu comunicado aqui]\n\nAtenciosamente,\nEquipe ${currentEntidade?.nome || 'da Clínica'}.`;

      setWhatsAppMessage(defaultMessage);
      setEmailSubject(defaultEmailSubject);
      setEmailMessage(defaultEmailBody);
      setSelectedPatients({});
      setSelectedRecipient('todos');
      setSelectAll(false);
      setCommunicationMethod('email');
    }
  }, [show, currentEntidade, mode]);

  // --- Funções de Lógica Interna (movidas de AgendasTab.js) ---

  const handleCheckboxChange = (appointmentId) => {
    setSelectedPatients(prev => ({ ...prev, [appointmentId]: !prev[appointmentId] }));
  };

  const handleRecipientChange = (event) => {
    const value = event.target.value;
    setSelectedRecipient(value);
    if (value === 'todos') {
      setSelectedPatients({});
      setSelectAll(false);
    }
  };

  const handleSelectAllChange = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    const newSelected = {};
    if (newSelectAll) {
      listItems.forEach(item => { newSelected[item.id] = true; });
    }
    setSelectedPatients(newSelected);
  };

  const getAgendamentosParaComunicacao = (checkMensagemEnviada = false) => {
    let possibleItems = listItems;
    if (mode === 'agenda') {
      if (checkMensagemEnviada) {
        // Para e-mails, evita reenvio se já foi enviado.
        // No modo comunicado, essa verificação não se aplica.
        possibleItems = possibleItems.filter(
          (agendamento) => agendamento.mensagem === null || agendamento.mensagem === 'enviado'
        );
      }
    }
    if (selectedRecipient === 'todos') return possibleItems;

    const selectedIds = Object.keys(selectedPatients).filter(id => selectedPatients[id]).map(id => parseInt(id, 10));
    return possibleItems.filter(item => selectedIds.includes(item.id));
  };

  const handleEnviarWhatsApp = async () => {
    if (!currentEntidade) {
        showAlertModal('Erro', 'Entidade não carregada.', 'danger');
        return;
    }
    if (!whatsAppMessage.trim()) {
        showAlertModal('Atenção', 'A mensagem de WhatsApp não pode estar vazia.', 'warning');
        return;
    }

    const agendamentosToSend = getAgendamentosParaComunicacao(false);

    if (agendamentosToSend.length === 0) {
        showAlertModal('Atenção', 'Nenhum destinatário válido selecionado para envio.', 'warning');
        return;
    }

    const messagesPayload = agendamentosToSend.map(item => {
        let paciente;
        if (mode === 'comunicado') {
            // No modo 'comunicado', o item já contém o objeto paciente completo.
            paciente = item.paciente;
        } else { // mode === 'agenda'
            // No modo 'agenda', o item é um agendamento e buscamos o paciente na lista completa.
            paciente = pacientesList.find(p => p.id === item.paciente_id);
        }

        if (!paciente || !paciente.dddcelular || !paciente.celular) {
            console.warn(`Paciente ${paciente?.nome || `com ID ${item.id}`} sem número de telefone válido. Pulando.`);
            return null;
        }

        const telefone = `55${paciente.dddcelular}${paciente.celular}`;
        let personalizedMessage = whatsAppMessage.replace(/{paciente_nome}/g, paciente.nome);

        if (mode === 'agenda') {
          const medico = todosMedicos.find(m => m.id === item.medico_id);
          const especialidade = especialidades.find(e => e.id === item.especialidade_id);
          personalizedMessage = personalizedMessage
            .replace(/{data_agendamento}/g, new Date(item.data_agendamento + 'T00:00:00').toLocaleDateString('pt-BR'))
            .replace(/{hora_marcada}/g, item.hora_marcada.slice(0, 5))
            .replace(/{medico_nome}/g, medico ? medico.nome : 'N/A')
            .replace(/{especialidade_nome}/g, especialidade ? especialidade.nome : 'N/A');
        }

        // A API de WhatsApp pode ou não precisar do agendamento_id, mas é bom ter.
        return { to: telefone, body: personalizedMessage, agendamento_id: mode === 'agenda' ? item.id : null };
    }).filter(Boolean);

    if (messagesPayload.length === 0) {
        showAlertModal('Atenção', 'Nenhum dos pacientes selecionados possui um número de telefone válido.', 'warning');
        return;
    }

    await new Promise(resolve => setTimeout(resolve, 1000)); // Simula delay da API

    showAlertModal('Sucesso', 'Mensagens de WhatsApp preparadas para envio pelo backend!', 'success');
  };

  const handleEnviarMensagens = async () => { // Refatorado para envio em lote
    if (!currentEntidade || !currentUser) {
      showAlertModal('Erro', 'Entidade ou usuário não carregado.', 'danger');
      return;
    }
    if (!emailSubject.trim() || !emailMessage.trim()) {
      showAlertModal('Atenção', 'O assunto e o corpo do e-mail não podem estar vazios.', 'warning');
      return;
    }

    const itemsToSend = getAgendamentosParaComunicacao(true);

    if (itemsToSend.length === 0) {
      showAlertModal('Atenção', 'Nenhum destinatário válido selecionado para envio de e-mail.', 'warning');
      return;
    }

    // Mapeia a lista de itens (agendamentos ou pacientes) para o formato de destinatário esperado pelo backend.
    const destinatariosPayload = itemsToSend.map(item => {
      let paciente;
      if (mode === 'comunicado') {
        // No modo 'comunicado', o item já contém o objeto paciente completo.
        paciente = item.paciente;
      } else { // mode === 'agenda'
        // No modo 'agenda', o item é um agendamento e buscamos o paciente na lista completa.
        paciente = pacientesList.find(p => p.id === item.paciente_id);
      }

      if (!paciente || !paciente.email) {
        console.warn(`Paciente ID ${paciente?.id || item.id} sem e-mail válido. Pulando.`);
        return null;
      }

      // Base do payload do destinatário
      const recipientData = {
        paciente_id: paciente.id,
        email: paciente.email,
        nome: paciente.nome, // O backend usará isso para substituir {paciente_nome}
        cpf: paciente.cpf, // O backend pode precisar para logs ou outras integrações
      };

      if (mode === 'agenda') {
        const medico = todosMedicos.find(m => m.id === item.medico_id);
        const especialidade = especialidades.find(e => e.id === item.especialidade_id);

        recipientData.agendamento_id = item.id; // Importante para o backend atualizar o status
        // Envia os dados para personalização no backend
        recipientData.data_agendamento = new Date(item.data_agendamento + 'T00:00:00').toLocaleDateString('pt-BR');
        recipientData.hora_marcada = item.hora_marcada.slice(0, 5);
        recipientData.medico_nome = medico ? medico.nome : 'N/A';
        recipientData.especialidade_nome = especialidade ? especialidade.nome : 'N/A';
      }

      return recipientData;
    }).filter(Boolean); // Remove os nulos (pacientes sem email)

    if (destinatariosPayload.length === 0) {
      showAlertModal('Atenção', 'Nenhum dos pacientes selecionados possui um endereço de e-mail válido.', 'warning');
      return;
    }

    // Monta o payload final para a chamada em lote
    const batchPayload = {
      entidade_id: currentEntidade.id,
      funcionario_id: currentUser.id,
      ds_assunto: emailSubject,
      ds_conteudo_template: emailMessage, // Envia o template. O backend fará a personalização.
      destinatarios: destinatariosPayload,
      mode: mode // Informa ao backend o contexto para a personalização das tags
    };

    // BACKEND: Este é o novo endpoint que você precisará criar.
    // Ele deve aceitar o payload acima e enfileirar os e-mails para envio.
    await axios.post('/api/mensagens/enviar-em-lote', batchPayload);

    showAlertModal('Sucesso', `${destinatariosPayload.length} e-mails foram colocados na fila de envio com sucesso!`, 'success');
  };

  const handleSendCommunication = async () => {
    setIsLoadingBulk(true);
    try {
      if (communicationMethod === 'email') {
        await handleEnviarMensagens();
      } else {
        await handleEnviarWhatsApp();
      }
      if (onSendComplete) {
        onSendComplete(); // Notifica o pai para re-buscar os dados
      }
      onHide(); // Fecha o modal
    } catch (error) {
      console.error("Erro ao despachar comunicação:", error);
      showAlertModal('Erro', `Falha no envio: ${error.message}`, 'danger');
    } finally {
      setIsLoadingBulk(false);
    }
  };

  const recipientCount = getAgendamentosParaComunicacao(communicationMethod === 'email').length;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <style>{`
        .comm-modal-header { background: linear-gradient(135deg,#1e3a5f,#2563eb); border-bottom: none; }
        .comm-modal-header .modal-title { color: #fff; font-weight: 700; }
        .comm-modal-header .btn-close { filter: invert(1) brightness(2); }
        .comm-method-option {
          display: flex; align-items: center; gap: 8px; padding: 10px 16px;
          border: 1.5px solid #e5e7eb; border-radius: 9px; cursor: pointer;
          transition: all .15s; margin-right: 10px;
        }
        .comm-method-option.active { border-color: #2563eb; background-color: #eff6ff; }
        .comm-method-option.disabled { opacity: .5; cursor: not-allowed; }
        .comm-summary-badge {
          display: inline-block; font-size: 12px; font-weight: 700;
          padding: 3px 10px; border-radius: 999px; background-color: #eff6ff;
          color: #2563eb; border: 1px solid #bfdbfe;
        }
        .comm-list { max-height: 320px; overflow-y: auto; border: 1px solid #e5e7eb; border-radius: 12px; background-color: #f9fafb; padding: 6px; }
        .comm-list-row {
          display: flex; align-items: center; gap: 12px; padding: 10px 12px;
          border-radius: 10px; background-color: #fff; margin-bottom: 6px;
          border: 1px solid #eef0f3; transition: border-color .15s, box-shadow .15s;
        }
        .comm-list-row:last-child { margin-bottom: 0; }
        .comm-list-row.selected { border-color: #93c5fd; box-shadow: 0 0 0 3px rgba(37,99,235,.08); }
        .comm-avatar {
          width: 36px; height: 36px; border-radius: 50%; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          color: #fff; font-weight: 700; font-size: 13px;
        }
        .comm-list-name { font-weight: 600; color: #1f2937; font-size: 13.5px; }
        .comm-list-meta { font-size: 11.5px; color: #6b7280; }
        .comm-list-badge {
          font-size: 10.5px; font-weight: 700; padding: 2px 9px; border-radius: 999px;
          background-color: #eff6ff; color: #2563eb; border: 1px solid #bfdbfe; white-space: nowrap;
        }
        .comm-list-empty { text-align: center; padding: 30px 10px; color: #9ca3af; font-size: 13px; }
      `}</style>

      <Modal.Header closeButton className="comm-modal-header">
        <Modal.Title>Comunicar Pacientes</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label className="fw-bold text-secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '.5px' }}>
            Método de Comunicação
          </Form.Label>
          <div className="d-flex">
            <label
              className={`comm-method-option ${communicationMethod === 'email' ? 'active' : ''}`}
              onClick={() => setCommunicationMethod('email')}
            >
              <Form.Check
                type="radio"
                name="communicationMethod"
                id="comm-method-email"
                value="email"
                checked={communicationMethod === 'email'}
                onChange={(e) => setCommunicationMethod(e.target.value)}
                className="mb-0"
              />
              📧 E-mail
            </label>
            <label className="comm-method-option disabled">
              <Form.Check type="radio" disabled className="mb-0" />
              💬 WhatsApp <span className="comm-summary-badge">Na Central de comunicação</span>
            </label>
          </div>
        </Form.Group>

        {communicationMethod === 'email' && (
          <>
            <Form.Group controlId="emailSubject" className="mt-3">
              <Form.Label>Assunto do E-mail:</Form.Label>
              <Form.Control
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="emailMessage" className="mt-3">
              <Form.Label>Corpo do E-mail:</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
              />
              <Form.Text className="text-muted">
                {mode === 'agenda'
                  ? 'Use as mesmas tags do WhatsApp para personalizar a mensagem.'
                  : 'Use {paciente_nome} para personalizar a mensagem.'
                }
              </Form.Text>
            </Form.Group>
          </>
        )}

        {communicationMethod === 'whatsapp' && (
          <Form.Group controlId="whatsappMessage" className="mt-3">
            <Form.Label>Mensagem de WhatsApp:</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={whatsAppMessage}
              onChange={(e) => setWhatsAppMessage(e.target.value)}
              maxLength={WHATSAPP_MESSAGE_LIMIT}
            />
            <div className="d-flex justify-content-between">
              <Form.Text className="text-muted" style={{ maxWidth: '80%' }}>
                {mode === 'agenda'
                  ? `Use {paciente_nome}, {data_agendamento}, {hora_marcada}, {medico_nome} e {especialidade_nome}.`
                  : `Use {paciente_nome}.`
                }
              </Form.Text>
              <Form.Text className={whatsAppMessage.length >= WHATSAPP_MESSAGE_LIMIT ? 'text-danger' : 'text-muted'}>
                {whatsAppMessage.length}/{WHATSAPP_MESSAGE_LIMIT}
              </Form.Text>
            </div>
          </Form.Group>
        )}
        <hr />
        <Form.Group controlId="recipientSelect">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <Form.Label className="mb-0">Destinatários:</Form.Label>
            <span className="comm-summary-badge">{recipientCount} selecionado(s)</span>
          </div>
          <Form.Select aria-label="Seleção de destinatários" value={selectedRecipient} onChange={handleRecipientChange}>
            <option value="todos">{mode === 'agenda' ? 'Todos os Pacientes Agendados' : 'Todos os Pacientes Cadastrados'}</option>
            <option value="individual">Individual</option>
          </Form.Select>
        </Form.Group>

        {/* ── LISTA MODERNIZADA (substitui a Table) ── */}
        <div className="mt-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Form.Check
              type="checkbox"
              label={<span style={{ fontSize: '12.5px', fontWeight: 600, color: '#374151' }}>Selecionar todos</span>}
              checked={selectAll}
              onChange={handleSelectAllChange}
              disabled={selectedRecipient === 'todos'}
            />
          </div>

          <div className="comm-list">
            {listItems.length === 0 && (
              <div className="comm-list-empty">Nenhum paciente encontrado para esta lista.</div>
            )}

            {listItems.map((item) => {
              const isChecked = selectedRecipient === 'todos' || selectedPatients[item.id] || false;
              const nome = item.paciente?.nome || 'Paciente não encontrado';
              return (
                <div key={item.id} className={`comm-list-row ${isChecked ? 'selected' : ''}`}>
                  <Form.Check
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleCheckboxChange(item.id)}
                    disabled={selectedRecipient === 'todos'}
                    className="mb-0"
                  />
                  <div className="comm-avatar" style={{ backgroundColor: getAvatarColor(item.id) }}>
                    {getInitials(nome)}
                  </div>
                  <div className="flex-grow-1">
                    <div className="comm-list-name">{nome}</div>
                    <div className="comm-list-meta">
                      {mode === 'agenda'
                        ? `${item.hora_marcada.slice(0, 5)} • ${item.tipo}`
                        : (item.paciente?.email || item.paciente?.celular ? 'Contato disponível' : 'Sem contato cadastrado')
                      }
                    </div>
                  </div>
                  <span className="comm-list-badge">{item.status}</span>
                </div>
              );
            })}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isLoadingBulk}>Fechar</Button>
        <Button variant="primary" onClick={handleSendCommunication} disabled={isLoadingBulk}>
          {isLoadingBulk && <Spinner as="span" animation="border" size="sm" className="me-2" />}
          {isLoadingBulk ? 'Enviando...' : 'Confirmar Envio'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CommunicationModal;
